'use client';

import type { ReactNode } from 'react';

import { cn } from '../../utils/cn';
import type { PublicProfileLayout } from '../public-profile-layout';

type ProfileSectionLabelProps = {
  layout: PublicProfileLayout;
  children: ReactNode;
  className?: string;
};

export function ProfileSectionLabel({
  layout,
  children,
  className,
}: ProfileSectionLabelProps) {
  return (
    <div
      className={cn(
        'font-bold uppercase text-[var(--faint)]',
        // Keep top margin on the first section — handover spaces CTA → modules
        // with seclabel margin (26/30/34), not zero.
        layout === 'cards' && 'mb-3 mt-[26px] text-xs tracking-[0.7px]',
        layout === 'centered' &&
          'mb-3.5 mt-[30px] text-center text-[11px] tracking-[1px]',
        // Classic: section gap comes from parent flex/grid gap (34px). Label only
        // needs the small gap down to content (6px mobile / 12px desktop).
        layout === 'classic' &&
          'mb-1.5 mt-0 text-[11px] tracking-[1.2px] @min-[880px]/profile:mb-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

type ProfileModuleShellProps = {
  layout: PublicProfileLayout;
  title: string;
  children: ReactNode;
  /** Full-width spanning modules (gallery/maps) on cards desktop. */
  fullWidth?: boolean;
  /** Flush media card — no body padding (video/maps). */
  flush?: boolean;
  className?: string;
  hideTitle?: boolean;
  /**
   * Cards entry modules: self-bordered rows on mobile, so dissolve the outer
   * shell below 880px (label stays outside via ProfileSectionLabel).
   */
  unframedOnMobile?: boolean;
};

/**
 * Layout-specific chrome around a module body.
 * Cards: bordered surface card. Centered/classic: label + bare content.
 */
export function ProfileModuleShell({
  layout,
  title,
  children,
  fullWidth,
  flush,
  className,
  hideTitle,
  unframedOnMobile,
}: ProfileModuleShellProps) {
  if (layout === 'cards') {
    return (
      <section
        className={cn(
          'min-w-0 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] @min-[880px]/profile:rounded-[18px]',
          fullWidth && '@min-[880px]/profile:col-span-2',
          unframedOnMobile &&
            '@max-[879px]/profile:overflow-visible @max-[879px]/profile:rounded-none @max-[879px]/profile:border-0 @max-[879px]/profile:bg-transparent',
          className,
        )}
      >
        {!hideTitle ? (
          <div
            className={cn(
              'px-[18px] pt-[18px] text-[12px] font-bold uppercase tracking-[0.7px] text-[var(--faint)] @min-[880px]/profile:mb-3.5 @min-[880px]/profile:flex @min-[880px]/profile:items-center @min-[880px]/profile:gap-2.5 @min-[880px]/profile:px-5 @min-[880px]/profile:pb-0 @min-[880px]/profile:pt-5 @min-[880px]/profile:text-[14px] @min-[880px]/profile:normal-case @min-[880px]/profile:tracking-normal @min-[880px]/profile:text-[var(--text)]',
              // Entry modules: mobile title is ProfileSectionLabel outside.
              unframedOnMobile && '@max-[879px]/profile:hidden',
            )}
          >
            {title}
          </div>
        ) : null}
        <div
          className={cn(
            flush
              ? 'p-0'
              : cn(
                  // Mobile: label is usually outside; body padding matches handover 18px.
                  // Desktop: uniform 20px card padding (chead margin via title mb).
                  'px-[18px] pb-[18px] pt-3 @min-[880px]/profile:px-5 @min-[880px]/profile:pb-5 @min-[880px]/profile:pt-0',
                  hideTitle && 'pt-[18px] @min-[880px]/profile:pt-5',
                  !hideTitle && '@max-[879px]/profile:pt-3',
                  unframedOnMobile && '@max-[879px]/profile:p-0',
                ),
          )}
        >
          {children}
        </div>
      </section>
    );
  }

  return (
    <section className={cn('w-full min-w-0', className)}>
      {!hideTitle ? (
        <ProfileSectionLabel layout={layout}>{title}</ProfileSectionLabel>
      ) : null}
      <div className="w-full min-w-0">{children}</div>
    </section>
  );
}
